import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const EMPTY = { announcements: [], unread: 0, pending: 0 };
const Ctx = createContext({ ...EMPTY, loading: true, refresh: () => {}, patch: () => {} });

export const useAnnouncements = () => useContext(Ctx);

const counts = (list) => ({
    announcements: list,
    unread: list.filter((a) => !a.presented_at).length,
    pending: list.filter(
        (a) => !a.presented_at || (a.requires_acknowledgment && !a.acknowledged_at),
    ).length,
});

/** Holds the caller's own active announcements (server already scopes them). */
export function AnnouncementsProvider({ children }) {
    const { user } = useAuth();
    const [state, setState] = useState(EMPTY);
    const [loading, setLoading] = useState(true);
    const userId = user?.id;
    const inFlight = useRef(false);

    const refresh = useCallback(async () => {
        if (!userId || inFlight.current) return;
        inFlight.current = true;
        try {
            const { data } = await api.get("/announcements/mine");
            setState(counts(Array.isArray(data?.announcements) ? data.announcements : []));
        } catch (err) {
            // Keep the last known list — never fabricate.
            console.debug("[announcements] refresh failed, keeping cached list:", err?.message || err);
        } finally {
            inFlight.current = false;
            setLoading(false);
        }
    }, [userId]);

    useEffect(() => {
        if (!userId) {
            setState(EMPTY);
            setLoading(false);
            return;
        }
        setLoading(true);
        refresh();
        const timer = setInterval(refresh, 120000);
        const onFocus = () => refresh();
        window.addEventListener("focus", onFocus);
        return () => {
            clearInterval(timer);
            window.removeEventListener("focus", onFocus);
        };
    }, [userId, refresh]);

    /** Merge a server response for one announcement into local state. */
    const patch = useCallback((id, changes) => {
        setState((s) => counts(s.announcements.map((a) => (a.id === id ? { ...a, ...changes } : a))));
    }, []);

    return (
        <Ctx.Provider value={{ ...state, loading, refresh, patch }}>{children}</Ctx.Provider>
    );
}
